import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components";
import { CALENDLY_URL } from "@/constants";
import { useTranslation } from "@/i18n";

// Icons aligned by index with t.services.cards; the last card is the AI & IoT highlight.
const CARD_ICONS = ["terminal", "public", "smartphone", "cloud", "hub", "neurology"];
const HIGHLIGHT_INDEX = 5;

export const Services: React.FC = () => {
  const t = useTranslation();

  const scrollTo = (elementId: string) => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <div id="leistungen"></div>
      <div className="py-[72px] md:py-[120px]">
        <div className="max-w-screen-xl m-auto px-[38px] 2xl:px-0">
          <p className="text-[#01C6FB] font-semibold tracking-[0.18em] uppercase text-[13px] md:text-[14px]">
            {t.services.overline}
          </p>
          <h2 className="mt-3 text-[#171A1F] font-bold text-[28px] leading-[38px] md:text-[44px] md:leading-[56px] max-w-3xl">
            {t.services.headline}
          </h2>
          <p className="mt-5 max-w-3xl text-[16px] leading-[26px] md:text-[18px] md:leading-[30px] text-base-600">
            {t.services.intro}
          </p>

          <div className="mt-[40px] md:mt-[56px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {t.services.cards.map((card, index) => {
              const highlight = index === HIGHLIGHT_INDEX;
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                  className={
                    highlight
                      ? "flex flex-col rounded-[20px] border-2 border-[#01C6FB] bg-[#F2FAFC] p-[28px] md:p-[32px] transition-shadow hover:shadow-lg"
                      : "flex flex-col rounded-[20px] border border-base-200 bg-[#FAFAFB] p-[28px] md:p-[32px] transition-shadow hover:shadow-lg"
                  }
                >
                  <span className="material-symbols-outlined text-[36px] text-[#01C6FB]">
                    {CARD_ICONS[index]}
                  </span>
                  <h3 className="mt-4 text-[20px] md:text-[22px] font-bold text-[#171A1F]">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-[25px] md:text-[16px] md:leading-[27px] text-base-600">
                    {card.text}
                  </p>
                  {highlight && (
                    <div className="mt-4 flex flex-wrap gap-4 text-[15px] font-semibold text-[#0195bd]">
                      <a
                        href="#ai-first"
                        className="cursor-pointer hover:underline"
                        onClick={(event) => {
                          event.preventDefault();
                          scrollTo("ai-first");
                        }}
                      >
                        {t.services.linkAi}
                      </a>
                      <a
                        href="#iot-triprock"
                        className="cursor-pointer hover:underline"
                        onClick={(event) => {
                          event.preventDefault();
                          scrollTo("iot-triprock");
                        }}
                      >
                        {t.services.linkIot}
                      </a>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          <div className="mt-[48px] md:mt-[64px] text-center">
            <p className="text-[17px] md:text-[20px] text-[#171A1F] font-medium max-w-2xl m-auto">
              {t.services.closing}
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-6"
            >
              <Button
                shape="circle"
                variant="ghost"
                className="border-2 border-[#01C6FB] text-[#0195bd] font-semibold px-8 hover:bg-[#01C6FB] hover:text-[#10141a]"
              >
                {t.services.cta}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
